/**
 * API Service Layer
 * Centralized API calls for the application
 */
import config from "../config";

class ApiService {
  constructor() {
    this.baseURL = config.API_BASE_URL;
  }

  /**
   * Generic fetch wrapper with error handling
   */
  /**
   * Generic fetch wrapper with error handling
   */
  async request(endpoint, options = {}) {
    const url =`${this.baseURL}${endpoint}`;

    // Check if body is FormData
    const isFormData = options.body instanceof FormData;

    const defaultOptions = {
      headers: {
        // Only set Content-Type for JSON, not for FormData
        ...(!isFormData && { "Content-Type": "application/json" }),
        ...options.headers,
      },
    };

    // For FormData, remove Content-Type entirely to let browser set it with boundary
    if (isFormData) {
      delete defaultOptions.headers["Content-Type"];
    }

    try {
      const response = await fetch(url, { ...defaultOptions, ...options });

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = {
            message: `Request failed with status: ${response.status}`,
            statusCode: response.status,
          };
        }
        throw new ApiError(
          errorData.message || `Request failed with status: ${response.status}`,
          response.status,
          errorData,
        );
      }

      return await response.json();
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(error.message || "Network error occurred", 0, {
        originalError: error,
      });
    }
  }

  /**
   * Generate video script from prompt
   */
  async generateScript(prompt, options = {}) {
    const {
      website,
      totalDuration,
      imgUrl,
      personalPrompt,
      dialogue,
      presenter,
      referenceImageUrl,
      referenceVideoUrl,
      aspectRatio,
      offers,
      logo,
      logoUrl,
      language,
      companyName,
      productImgUrls,
      modelImgUrls,
      backgroundUrls,
      aiMode,
    } = options;

    if (website) {
      return this.request("/web/search", {
        method: "POST",
        body: JSON.stringify({
          website,
          originalPrompt: prompt,
          totalDuration,
          ...(imgUrl && { imgUrl }),
          ...(personalPrompt !== undefined && { personalPrompt }),
          ...(dialogue !== undefined && { dialogue }),
          ...(presenter !== undefined && { presenter }),
          ...(referenceImageUrl && { referenceImageUrl }),
          ...(referenceVideoUrl && { referenceVideoUrl }),
          ...(aspectRatio && { aspectRatio }),
          ...(offers !== undefined && { offers }),
          ...(logo !== undefined && { logo }),
          ...(logoUrl !== undefined && { logoUrl }),
          ...(language && Array.isArray(language) && language.length > 0 && { language }),
          ...(companyName !== undefined && { companyName }),
          ...(productImgUrls?.length > 0 && { productImgUrls }),
          ...(modelImgUrls?.length > 0 && { modelImgUrls }),
          ...(backgroundUrls?.length > 0 && { backgroundUrls }),
          ...(aiMode && { aiMode }),
        }),
      });
    }

    return this.request("/deepSeek/generate", {
      method: "POST",
      body: JSON.stringify({
        originalPrompt: prompt,
        totalDuration,
        ...(imgUrl && { imgUrl }),
        ...(personalPrompt !== undefined && { personalPrompt }),
        ...(dialogue !== undefined && { dialogue }),
        ...(presenter !== undefined && { presenter }),
        ...(referenceImageUrl && { referenceImageUrl }),
        ...(referenceVideoUrl && { referenceVideoUrl }),
        ...(aspectRatio && { aspectRatio }),
        ...(offers !== undefined && { offers }),
        ...(logo !== undefined && { logo }),
        ...(logoUrl !== undefined && { logoUrl }),
        ...(language && Array.isArray(language) && language.length > 0 && { language }),
        ...(companyName !== undefined && { companyName }),
        ...(productImgUrls !== undefined && { productImgUrls }),
        ...(modelImgUrls !== undefined && { modelImgUrls }),
        ...(backgroundUrls !== undefined && { backgroundUrls }),
        ...(aiMode && { aiMode }),
      }),
    });
  }

  /**
   * Upload image file
   */
  async uploadImage(file) {
    const formData = new FormData();
    formData.append("file", file);

    const response = await fetch(`${this.baseURL}/bucket/upload`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new ApiError("Image upload failed", response.status);
    }

    return await response.json();
  }

  /**
   * Upload logo image
   */
  async uploadLogo(file) {
    const formData = new FormData();
    formData.append("image", file);
    console.log("Uploading logo with formData:", formData);

    return this.request("/video/upload-image", {
      method: "POST",
      body: formData,
    });
  }

  async uploadProductImage(file) {
    const formData = new FormData();
    formData.append("image", file);
    return this.request("/video/upload-image", {
      method: "POST",
      body: formData,
    });
  }

  async uploadModelImage(file) {
    const formData = new FormData();
    formData.append("image", file);
    return this.request("/video/upload-image", {
      method: "POST",
      body: formData,
    });
  }

  /**
   * Upload background image
   */
  async uploadBackgroundImage(file) {
    const formData = new FormData();
    formData.append("image", file);
    return this.request("/video/upload-image", {
      method: "POST",
      body: formData,
    });
  }

  async uploadReferenceImage(file) {
    const formData = new FormData();
    formData.append("image", file);
    return this.request("/video/upload-image", {
      method: "POST",
      body: formData,
    });
  }

  async uploadReferenceVideo(file) {
    const formData = new FormData();
    formData.append("video", file);
    return this.request("/video/upload-video", {
      method: "POST",
      body: formData,
    });
  }

  async uploadVoice(file) {
    const formData = new FormData();
    formData.append("audio", file);
    return this.request("/video/upload-audio", {
      method: "POST",
      body: formData,
    });
  }

  /**
   * Create a new video project
   */
  async createProject(scenes, originalPrompt, clientId, userId, options = {}) {
    const {
      aspectRatio,
      sceneDuration,
      initialImageUrl,
      referenceImageUrl,
      referenceVideoUrl,
      logoUrl,
      language,
      productImgUrls,
modelImgUrls,
backgroundUrls,
isDirectMode,
voiceSampleUrl,
} = options;

    const requestBody = {
      scenes: scenes.map((s) => ({
        sceneNumber: s.sceneNumber,
        dialogue: s.editedDialogue ?? s.dialogue ?? "",
        visualDescription: s.visualDescription || s.scriptText || s.fullText,
      })),
      clientId,
      userId,
      originalPrompt,
    };

    if (aspectRatio) requestBody.aspectRatio = aspectRatio;
    if (sceneDuration) {
      if (isDirectMode) {
        requestBody.sceneDuration = sceneDuration; // Direct mode
      } else {
        requestBody.totalDuration = sceneDuration; // AI Enhanced mode
      }
    }
    if (logoUrl) requestBody.logoUrl = logoUrl;
    const resolvedReferenceImageUrl =
      referenceImageUrl ||
      initialImageUrl ||
      productImgUrls?.[0] ||
      backgroundUrls?.[0] ||
      (Array.isArray(logoUrl) ? logoUrl[0] : logoUrl);
    if (resolvedReferenceImageUrl) requestBody.initialImageUrl = resolvedReferenceImageUrl;
    if (referenceVideoUrl) requestBody.referenceVideoUrl = referenceVideoUrl;
    if (language && Array.isArray(language) && language.length > 0) {
      requestBody.language = language;
    }
    if (productImgUrls?.length > 0) requestBody.productUrl = productImgUrls;
    
if (modelImgUrls?.length > 0)   requestBody.modelUrl   = modelImgUrls;
if (backgroundUrls?.length > 0) requestBody.backgroundUrl = backgroundUrls;
    if (voiceSampleUrl) requestBody.voiceSampleUrl = voiceSampleUrl;
    if (options.aiMode) requestBody.aiMode = options.aiMode;
    console.log(
      "📦 createProject requestBody:",
      JSON.stringify(requestBody, null, 2),
    );

    return this.request("/video-projects", {
      method: "POST",
      body: JSON.stringify(requestBody),
    });
  }

  /**
   * Get project details
   */
  async getProject(projectId) {
    return this.request(`/video-projects/${projectId}`);
  }

  /**
   * Generate a scene
   */
  async generateScene(projectId, sceneNumber, options = {}) {
    const requestBody = {};
    if (options.dialogue !== undefined) {
      requestBody.dialogue = options.dialogue;
    }

    return this.request(
      `/video-projects/${projectId}/scenes/${sceneNumber}/generate`,
      {
        method: "POST",
        body:
          Object.keys(requestBody).length > 0
            ? JSON.stringify(requestBody)
            : undefined,
      },
    );
  }

  async generateSceneFromSubject(projectId, sceneNumber, options = {}) {
    const requestBody = {};
    if (options.prompt) requestBody.prompt = options.prompt;
    if (options.subjects?.length > 0) requestBody.subjects = options.subjects;
    if (options.aspectRatio) requestBody.aspectRatio = options.aspectRatio;
    if (options.durationSeconds) requestBody.durationSeconds = options.durationSeconds;

    return this.request(
      `/video-projects/${projectId}/scenes/${sceneNumber}/generate-from-subject`,
      {
        method: "POST",
        body: JSON.stringify(requestBody),
      },
    );
  }

  async generateSceneSeedance(projectId, sceneNumber) {
    return this.request("/seedance/generate-from-project", {
      method: "POST",
      body: JSON.stringify({
        projectId,
        sceneNumber: String(sceneNumber),
      }),
    });
  }

  /**
   * Approve a scene
   */
  async approveScene(projectId, sceneNumber, refinementIndex = null) {
    const body = refinementIndex !== null ? { refinementIndex } : {};
    return this.request(
      `/video-projects/${projectId}/scenes/${sceneNumber}/approve`,
      {
        method: "POST",
        body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
      },
    );
  }

  /**
   * Refine a scene
   */
  async refineScene(projectId, sceneNumber, options = {}) {
    const requestBody = {};
    if (options.visualFeedback?.trim()) {
      requestBody.visualFeedback = options.visualFeedback;
    }
    // Include dialogue if it's explicitly provided (even if empty string)
    if (options.dialogue !== undefined) {
      requestBody.dialogue = options.dialogue;
    }

    return this.request(
      `/video-projects/${projectId}/scenes/${sceneNumber}/refine-visual`,
      {
        method: "POST",
        body: JSON.stringify(requestBody),
      },
    );
  }

  /**
   * Reorder thumbnails — move liked thumbnail to first position in DB
   */
  async reorderThumbnails(projectId, likedUrl) {
    return this.request(`/video-projects/${projectId}/thumbnails/reorder`, {
      method: 'POST',
      body: JSON.stringify({ likedUrl }),
    });
  }

  /**
   * Combine a selected thumbnail as an intro with the final video
   */
  async combineThumbnailWithVideo(projectId, thumbnailUrl, videoUrl, introDurationSeconds = 2) {
    return this.request(`/video-projects/${projectId}/combine-thumbnail`, {
      method: 'POST',
      body: JSON.stringify({ thumbnailUrl, videoUrl, introDurationSeconds }),
    });
  }

  /**
   * Finalize project
   */
  async finalizeProject(projectId, generateThumbnails = true, hookLine = null) {
    return this.request(`/video-projects/${projectId}/finalize`, {
      method: "POST",
      body: JSON.stringify({
        generateThumbnails,
        ...(hookLine && { hookLine }),
      }),
    });
  }
}

/**
 * Custom API Error class
 */
export class ApiError extends Error {
  constructor(message, statusCode = 0, data = {}) {
    super(message);
    this.name = "ApiError";
    this.statusCode = statusCode;
    this.data = data;

    // Extract error details from API response
    this.isRetryable = data.isRetryable !== undefined ? data.isRetryable : true;
    this.retryAfter = data.retryAfter || null;
    this.errorCode = data.errorCode || null;
  }
}

const apiService = new ApiService();
export default apiService;
